import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import Svg, { Path } from 'react-native-svg';

interface DotPaginationProps {
  totalDots: number;
  activeDot: number;
  onDotPress?: (index: number) => void;
}

const LeftArrow: React.FC<{ active?: boolean }> = ({ active = false }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M12.8417 6.175L9.025 10L12.8417 13.825L11.6667 15L6.66667 10L11.6667 5L12.8417 6.175Z" fill={active ? '#034895' : '#B1B1B1'} />
  </Svg>
);

const RightArrow = () => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path d="M7.15833 13.825L10.975 10L7.15833 6.175L8.33333 5L13.3333 10L8.33333 15L7.15833 13.825Z" fill="#034895" />
  </Svg>
);

const ActiveRectangle = () => (
  <Svg width="32" height="24" viewBox="0 0 32 24" fill="none">
    <Path d="M4 12C4 9.79086 5.79086 8 8 8H24C26.2091 8 28 9.79086 28 12V12C28 14.2091 26.2091 16 24 16H8C5.79086 16 4 14.2091 4 12V12Z" fill="#004895" />
  </Svg>
);

const InactiveDot = () => (
  <Svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <Path d="M4 8C6.20914 8 8 6.20914 8 4C8 1.79086 6.20914 0 4 0C1.79086 0 0 1.79086 0 4C0 6.20914 1.79086 8 4 8Z" fill="#838383" />
  </Svg>
);

const DotPagination: React.FC<DotPaginationProps> = ({
  totalDots,
  activeDot,
  onDotPress,
}) => {
  const handlePrevious = () => {
    if (activeDot > 0 && onDotPress) {
      onDotPress(activeDot - 1);
    }
  };

  const handleNext = () => {
    if (activeDot < totalDots - 1 && onDotPress) {
      onDotPress(activeDot + 1);
    }
  };

  return (
    <View style={styles.container}>
      {/* Left Arrow */}
      <TouchableOpacity
        onPress={handlePrevious}
        disabled={activeDot === 0}
        style={[styles.arrow, activeDot === 0 && styles.arrowDisabled]}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <LeftArrow active={activeDot !== 0} />
      </TouchableOpacity>

      {/* Dots */}
      <View style={styles.dotsContainer}>
        {Array.from({ length: totalDots }).map((_, index) => (
          <TouchableOpacity
            key={index}
            onPress={() => onDotPress && onDotPress(index)}
            style={styles.dotWrapper}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            {index === activeDot ? <ActiveRectangle /> : <InactiveDot />}
          </TouchableOpacity>
        ))}
      </View>

      {/* Right Arrow */}
      <TouchableOpacity
        onPress={handleNext}
        disabled={activeDot === totalDots - 1}
        style={[styles.arrow, activeDot === totalDots - 1 && styles.arrowDisabled]}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <RightArrow />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  arrow: {
    padding: 4,
  },
  arrowDisabled: {
    opacity: 0.3,
  },
  dotsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
  },
  dotWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default DotPagination;
